import * as React from 'react';

import { cn } from '@/_shared/lib/utils';

export type CalloutVariant = 'highlight' | 'info' | 'success' | 'warning' | 'error';

// Every variant shares one design — a soft tinted fill with a leading accent
// rail — and differs only in color. Colors come from the semantic severity
// triads (bg = soft fill, fg = strong text) so the block re-themes in dark.
const VARIANT_CLASSES: Record<CalloutVariant, string> = {
  highlight: 'bg-highlight-bg text-info',
  info: 'bg-info-bg text-info',
  success: 'bg-success-bg text-success',
  warning: 'bg-warning-bg text-warning',
  error: 'bg-danger-bg text-danger',
};

// The leading accent rail, in each variant's strong shade.
const RAIL_CLASSES: Record<CalloutVariant, string> = {
  highlight: 'bg-highlight',
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-danger',
};

interface CalloutProps extends React.ComponentProps<'div'> {
  variant?: CalloutVariant;
}

/**
 * Callout — an inline message block. Every variant uses the same design: a soft
 * tinted fill with a leading accent rail, differing only in color. The body
 * inherits the variant's text color via `currentColor`, so links tint automatically.
 */
function Callout({ variant = 'info', className, children, ...props }: CalloutProps): React.JSX.Element {
  return (
    <div
      data-slot="callout"
      data-variant={variant}
      className={cn(
        'flex items-start gap-3 rounded-lg py-3 pr-4 pl-3 text-sm leading-relaxed',
        VARIANT_CLASSES[variant],
        className,
      )}
      {...props}
    >
      <span aria-hidden className={cn('w-0.5 shrink-0 self-stretch rounded-full', RAIL_CLASSES[variant])} />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export { Callout };
